import styled, { css, DefaultTheme } from 'styled-components';
import { Variant, TextProps, Color } from './types';

const defaults = {
  variant: 'b400' as Variant,
  color: 'neutral' as Color,
};

export const Text = styled.p<TextProps>`
  font-family: ${({ theme }) => theme.font.family.heading};
  margin: 0;

  ${({ variant, theme }) => variantMapping(variant || defaults.variant, theme)}

  color: ${({ $color, theme }) =>
    $color ? colorMapping($color, theme) : colorMapping(defaults.color, theme)};

  ${({ variant }) =>
    variant &&
    variant === 'b400-link' &&
    css`
      text-decoration: underline;
    `}

  ${({ variant }) =>
    variant &&
    variant === 'overline' &&
    css`
      text-transform: uppercase;
    `};
`;

function variantMapping(variant: Variant, theme: DefaultTheme) {
  const propsMap = {
    h800: {
      size: theme.font.size.m7,
      weight: theme.font.weight.bold,
      height: theme.font.lineHeight.tiny,
    },
    h700: {
      size: theme.font.size.m6,
      weight: theme.font.weight.bold,
      height: theme.font.lineHeight.small,
    },
    h600: {
      size: theme.font.size.m5,
      weight: theme.font.weight.bold,
      height: theme.font.lineHeight.tiny,
    },
    h500: {
      size: theme.font.size.m4,
      weight: theme.font.weight.bold,
      height: theme.font.lineHeight.medium,
    },
    h400: {
      size: theme.font.size.base,
      weight: theme.font.weight.regular,
      height: theme.font.lineHeight.medium,
    },
    sub500: {
      size: theme.font.size.m4,
      weight: theme.font.weight.regular,
      height: theme.font.lineHeight.medium,
    },
    sub400: {
      size: theme.font.size.m3,
      weight: theme.font.weight.medium,
      height: theme.font.lineHeight.tiny,
    },
    sub300: {
      size: theme.font.size.m3,
      weight: theme.font.weight.medium,
      height: theme.font.lineHeight.small,
    },
    sub200: {
      size: theme.font.size.m3,
      weight: theme.font.weight.regular,
      height: theme.font.lineHeight.small,
    },
    b400: {
      size: theme.font.size.m3,
      weight: theme.font.weight.regular,
      height: theme.font.lineHeight.large,
    },
    b500: {
      size: theme.font.size.m4,
      weight: theme.font.weight.regular,
      height: theme.font.lineHeight.small,
    },
    'b400-link': {
      size: theme.font.size.m3,
      weight: theme.font.weight.regular,
      height: theme.font.lineHeight.large,
    },
    'b400-bold': {
      size: theme.font.size.m3,
      weight: theme.font.weight.bold,
      height: theme.font.lineHeight.large,
    },
    caption: {
      size: theme.font.size.m2,
      weight: theme.font.weight.medium,
      height: theme.font.lineHeight.medium,
    },
    'caption-bold': {
      size: theme.font.size.m2,
      weight: theme.font.weight.bold,
      height: theme.font.lineHeight.medium,
    },
    overline: {
      size: theme.font.size.m1,
      weight: theme.font.weight.bold,
      height: theme.font.lineHeight.large,
    },
  };

  return css`
    font-size: ${propsMap[variant].size};
    font-weight: ${propsMap[variant].weight};
    line-height: ${propsMap[variant].height};
  `;
}

function colorMapping(color: Color, theme: DefaultTheme) {
  const propsMap = {
    brand: theme.color.text.brand.default,
    'brand-strong': theme.color.text.brand.strong,
    neutral: theme.color.text.neutral.default,
    'neutral-inverted': theme.color.text.neutral.inverted,
    'neutral-weak': theme.color.text.neutral.weak,
    'neutral-strong': theme.color.text.neutral.strong,
    danger: theme.color.text.danger.default,
    'danger-strong': theme.color.text.danger.strong,
    success: theme.color.text.success.default,
    'success-strong': theme.color.text.success.strong,
    'support-purple': theme.color.support.purple.strong,
    'support-blue': theme.color.support.blue.strong,
    'support-orange': theme.color.support.orange.strong,
    'support-green': theme.color.support.green.strong,
  };

  return propsMap[color];
}
